import {FC, MouseEventHandler, useCallback, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {getEventId, selectEventStatus} from '~/common/fabric/reducers/eviteEvent/selectors';
import {selectTemplateName} from '~/common/fabric/reducers/fabricTemplate/selector';
import {Button} from '~library/atoms/Button';
import {ContentIdentifiers} from '~library/shared/enums/ContentIdentifiers';
import {TrackingEvents} from '~library/shared/enums/TrackingEvents';
import {DiamondIcon} from '~library/svg/DiamondIcon';
import {sendTrackingData} from '~library/utils/tracking';

import {white} from '~libSass/base/_exports.sass';

export interface UpsellButtonsProps {
  toggleDowngradeModal: MouseEventHandler<HTMLButtonElement>;
  handleGoPremiumClick: MouseEventHandler<HTMLButtonElement>;
  isLoading: boolean;
  isDesktop: boolean;
  keepBasicText?: string;
  premiumText?: string;
  okDataQaId?: string;
  cancelDataQaId?: string;
}

export const UpsellButtons: FC<UpsellButtonsProps> = ({
  toggleDowngradeModal,
  handleGoPremiumClick,
  isLoading,
  isDesktop,
  keepBasicText = 'Stay in basic',
  premiumText = 'Switch to Premium',
  okDataQaId = 'nav-bottom-upgrade',
  cancelDataQaId = 'nav-bottom-keep-it-basic',
}) => {
  const templateName = useSelector(selectTemplateName);
  const eventId = useSelector(getEventId);
  const eventStatus = useSelector(selectEventStatus);

  useEffect(() => {
    sendTrackingData(TrackingEvents.ContentLoadTracking, {
      identifier: ContentIdentifiers.UpsellModalStep4Copy,
      attributes: {
        templateId: templateName,
        eventId: eventId,
        status: eventStatus,
      },
    });
  }, [templateName, eventId, eventStatus]);

  const handleUpgradeClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      sendTrackingData(TrackingEvents.CfUpsellTracking, {
        identifier: ContentIdentifiers.UpsellModalStep4Copy,
        templateId: templateName,
        eventId: eventId,
        status: eventStatus,
      });
      handleGoPremiumClick(e);
    },
    [templateName, eventId, eventStatus, handleGoPremiumClick]
  );

  return [
    <Button
      id="keepItBasic"
      className="card-design__nav-bottom__cta narrow"
      data-qa-id={cancelDataQaId}
      variant="secondary"
      size="large"
      onClick={toggleDowngradeModal}
      disabled={isLoading}
      key="keep-it-basic"
      fullWidth={!isDesktop}
    >
      {keepBasicText}
    </Button>,
    <Button
      id="upgrade"
      className="card-design__nav-bottom__cta"
      data-qa-id={okDataQaId}
      variant="primary"
      size="large"
      onClick={handleUpgradeClick}
      disabled={isLoading}
      key="upgrade"
      fullWidth={!isDesktop}
    >
      <DiamondIcon color={white} />
      {premiumText}
    </Button>,
  ];
};
